import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CssBaseline,
  GlobalStyles,
  PaletteMode,
  Snackbar,
  Stack,
  useMediaQuery,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";

import Header from "./Header";
import Main from "./Main";
import ProgressDialog from "./ProgressDialog";
import ActiveUploadProgress from "./ActiveUploadProgress";
import { TransferQueueProvider } from "./app/transferQueue";
import getExpressiveThemeOptions from "./theme";

const globalStyles = (
  <GlobalStyles styles={{ "html, body, #root": { height: "100%" } }} />
);

function App() {
  const [search, setSearch] = useState("");
  const [showProgressDialog, setShowProgressDialog] = React.useState(false);
  const [error, setError] = useState<Error | null>(null);
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  const [mode, setMode] = useState<PaletteMode>("light");

  useEffect(() => {
    const storedMode =
      typeof window !== "undefined"
        ? window.localStorage.getItem("voiiddrive-color-mode")
        : null;

    if (storedMode === "light" || storedMode === "dark") {
      setMode(storedMode);
    } else {
      setMode(prefersDark ? "dark" : "light");
    }
  }, [prefersDark]);

  const theme = useMemo(
    () => createTheme(getExpressiveThemeOptions(mode)),
    [mode]
  );

  const handleToggleColorMode = useCallback(() => {
    setMode((previous) => {
      const next = previous === "light" ? "dark" : "light";
      if (typeof window !== "undefined") {
        window.localStorage.setItem("voiiddrive-color-mode", next);
      }
      return next;
    });
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {globalStyles}
      <TransferQueueProvider>
        <Stack
          sx={{
            height: "100%",
            backgroundColor: (theme) => theme.palette.background.default,
          }}
        >
          <Header
            search={search}
            onSearchChange={(newSearch: string) => setSearch(newSearch)}
            setShowProgressDialog={setShowProgressDialog}
            mode={mode}
            onToggleColorMode={handleToggleColorMode}
          />
          <Main search={search} onError={setError} />
        </Stack>
        <Snackbar
          autoHideDuration={5000}
          open={Boolean(error)}
          message={error?.message}
          onClose={() => setError(null)}
        />
        <ProgressDialog
          open={showProgressDialog}
          onClose={() => setShowProgressDialog(false)}
        />
        <ActiveUploadProgress />
      </TransferQueueProvider>
    </ThemeProvider>
  );
}

export default App;
